/** 通知只读详情：消息事实与全部安全设备记录分开展示，不把打开归因到设备。 */
import { type PushAnalyticsNotification, usePushAnalyticsNotification } from '~/data/queries';
import { fmtDateTimeInTimeZone } from '~/lib/format';
import { formatPushTemplateName } from '~/lib/push-template-display';
import { Drawer } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { Table } from '~/ui/Table';

export const PUSH_ANALYTICS_CATEGORY_LABELS: Record<string, string> = {
  OFFER: '营销优惠',
  NOVEL_UPDATE: '书籍更新',
  NOVEL_DELISTED: '书籍下架',
  FEEDBACK: '反馈',
  TASK: '任务',
  SUBSCRIPTION: '订阅',
};
export const MESSAGE_STATUS_LABELS = {
  PENDING: '待分派',
  DISPATCHED: '已分派',
  OPENED: '已记录打开',
};
export const DELIVERY_STATUS_LABELS = {
  PENDING: '排队中',
  SENDING: '发送中',
  SENT: '供应商已接受',
  FAILED_RETRIABLE: '可重试失败',
  FAILED_PERMANENT: '永久失败',
};
export const TEST_KIND_LABELS = {
  BUSINESS: '未识别为测试',
  TEMPLATE_TEST: '模板测试',
  DEV_QA_1151: '特定 QA 样例',
  INTERNAL_ACCOUNT: '当前内部账号',
  UNKNOWN_AFTER_ANONYMIZATION: '匿名化后测试属性未知',
};
export function deviceDataLabel(notification: PushAnalyticsNotification): string {
  if (notification.device_data_state === 'CLEARED_PRIVACY')
    return '设备明细已因隐私清理；无法判定历史投递结果';
  if (notification.device_data_state === 'NO_RECORDS')
    return '暂无设备投递记录；可能未建记录或存在无法追溯的历史清理';
  return '存在设备记录，不保证历史明细完整';
}

export function PushAnalyticsNotificationDetail({
  id,
  appId,
  timezone,
  onClose,
}: { id: string; appId?: string; timezone: string; onClose: () => void }) {
  const query = usePushAnalyticsNotification(id, appId);
  const notification = query.data?.notification;
  const time = (value: string | null) => (value ? fmtDateTimeInTimeZone(value, timezone) : '—');
  return (
    <Drawer
      title="通知与设备详情"
      subtitle={id}
      icon="IconPaperPlane"
      width={860}
      onClose={onClose}
    >
      <div style={{ padding: '0 18px 18px' }}>
        {query.isPending && (
          <p role="status" className="muted">
            通知详情加载中…
          </p>
        )}
        {query.isError && (
          <div
            role="alert"
            style={{ padding: '14px 0', color: 'var(--neg)', overflowWrap: 'anywhere' }}
          >
            无法读取通知详情 · {query.error.message}
            。不存在、超出近期窗口或范围不符均可能不可查，不能据此断定消息从未创建。
            <button type="button" className="btn btn-sm" onClick={() => void query.refetch()}>
              重试
            </button>
          </div>
        )}
        {query.data && !query.isError && notification && (
          <>
            <FormRow label="消息身份">
              <span className="mono" style={{ overflowWrap: 'anywhere' }}>
                {notification.notification_id}
              </span>
            </FormRow>
            <FormRow label="App / 业务 UID">
              {notification.app_name} ·{' '}
              <span className="mono">{notification.user_uid ?? '身份已匿名化或关联已清理'}</span>
            </FormRow>
            <FormRow label="模板 / 实际渲染语言">
              <span title={notification.template_slug}>
                {formatPushTemplateName(notification.template_slug)}
              </span>{' '}
              · <span className="mono">{notification.rendered_language}</span>
            </FormRow>
            <FormRow label="消息类别">
              {PUSH_ANALYTICS_CATEGORY_LABELS[notification.category]} ·{' '}
              <span className="mono">{notification.category}</span>
            </FormRow>
            <FormRow label="逻辑状态 / 测试分类">
              {MESSAGE_STATUS_LABELS[notification.status]} ·{' '}
              {TEST_KIND_LABELS[notification.test_data_kind]}
            </FormRow>
            <FormRow label="创建 / 分派时刻">
              <div className="mono">
                {time(notification.created_at)}
                <br />
                {time(notification.sent_at)}
              </div>
            </FormRow>
            <FormRow label="首次已读 / 首次记录打开">
              <div className="mono">
                {time(notification.read_at)}
                <br />
                {notification.opened_at ? time(notification.opened_at) : '尚未记录打开'}
              </div>
            </FormRow>
            <FormRow label="链接 / 发送截止">
              {notification.has_link ? '有持久化链接' : '无持久化链接'} ·{' '}
              <span className="mono">{time(notification.deliver_before)}</span>
            </FormRow>
            <p className="chip chip-warn">
              当前客户端存在后台通知点击未回传缺口；尚未记录打开不证明未点击，已读不等于打开。
            </p>
            <p className="muted">
              {deviceDataLabel(notification)}
              。设备详情展示全部设备，不受列表设备筛选影响；供应商接受不表示手机实际收到。
            </p>
            {notification.device_summary && (
              <p className="mono">
                当前全部设备 {notification.device_summary.device_total} · 供应商接受{' '}
                {notification.device_summary.provider_accepted} · 可重试失败{' '}
                {notification.device_summary.device_failed_retriable} · 永久失败{' '}
                {notification.device_summary.device_failed_permanent}
              </p>
            )}
            {query.data.delivery_records_truncated && (
              <p role="status" className="chip chip-warn">
                仅展示前 200 条设备记录，不能视为全部设备。
              </p>
            )}
            <Table className="tbl">
              <thead>
                <tr>
                  <th>平台 / 供应商</th>
                  <th>当前状态</th>
                  <th>创建 / 领取</th>
                  <th>接受 / 最近保留失败时间</th>
                  <th>安全失败原因</th>
                </tr>
              </thead>
              <tbody>
                {query.data.deliveries.map((delivery) => (
                  <tr key={delivery.id}>
                    <td className="mono">
                      {delivery.platform} / {delivery.vendor}
                    </td>
                    <td>{DELIVERY_STATUS_LABELS[delivery.delivery_status]}</td>
                    <td className="mono">
                      {time(delivery.created_at)}
                      <br />
                      {time(delivery.claimed_at)}
                    </td>
                    <td className="mono">
                      {time(delivery.sent_at)}
                      <br />
                      {time(delivery.failed_at)}
                    </td>
                    <td>
                      <span className="mono">{delivery.failure_reason.code}</span> ·{' '}
                      {delivery.failure_reason.label}
                    </td>
                  </tr>
                ))}
                {query.data.deliveries.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: 24, color: 'var(--fg-muted)' }}>
                      {deviceDataLabel(notification)}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <p className="muted">
              详情统计时刻 <span className="mono">{time(query.data.statistics_at)}</span>
              ；与聚合快照独立读取。当前结果以投递状态为准，不是完整尝试历史；记录打开可与设备失败同时存在。
            </p>
          </>
        )}
      </div>
    </Drawer>
  );
}
